import React from 'react';
import PropTypes from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
// @material-ui/core components
import userProfileStyles from 'assets/jss/material-dashboard-pro-react/views/userProfileStyles';
import withStyles from '@material-ui/core/styles/withStyles';
// material-ui icons
import Cancel from '@material-ui/icons/Cancel';
import Done from '@material-ui/icons/Done';
import Refresh from '@material-ui/icons/Refresh';
// global variables
import { reviewReportStatus } from 'config';
// core components
import Button from 'components/UI/CustomButtons/Button';
import Card from 'components/UI/Card/Card';
import CardBody from 'components/UI/Card/CardBody';
import GridContainer from 'components/UI/Grid/GridContainer';
import GridItem from 'components/UI/Grid/GridItem';
import Snackbar from 'components/UI/Snackbar/Snackbar';
// our components
import { LoadingAnimation } from 'components/common/Loading';
import RouterContext from 'helpers/routerContext';
import SectionDivider from 'components/common/SectionDivider';
import WithPermission from 'components/User/WithPermission';
import { REVIEWS, USER } from 'routes/paths';
import dateFormat from 'helpers/dateHelper';

const resolvePermissions = [
  'reviews.resolve_reviewreport',
  'resolve_reviewreport',
];
const dismissPermissions = [
  'reviews.dismiss_reviewreport',
  'dismiss_reviewreport',
];
const viewReviewPermissions = ['reviews.view_review', 'view_review'];

class ReviewReportDetails extends React.Component {
  static contextType = RouterContext;

  handleReadUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  handleReadReview = id => {
    const history = this.context;
    history.push(formatRoute(REVIEWS.DETAILS, { id }));
  };

  renderUser = (user, idPrefix) => {
    const { classes } = this.props;
    if (!user) {
      return <p>-</p>;
    }
    return (
      <WithPermission
        alt={<p>{user.fullname || user.id}</p>}
        allowPermissions={['view_user']}
      >
        <Button
          id={`${idPrefix}-${user.id}`}
          simple
          color="primary"
          style={{ padding: 0, margin: 0, textAlign: 'left' }}
          className={classes.adLinks}
          onClick={() => this.handleReadUser(user.id)}
        >
          {user.fullname || user.id}
        </Button>
      </WithPermission>
    );
  };

  renderStatus = status => {
    const { t } = this.props;
    return status ? t(`review_report.status.${status}`) : t('common.unknown');
  };

  renderReason = reason => {
    const { t } = this.props;
    return reason ? t(`review_report.reasons.${reason}`) : t('common.unknown');
  };

  renderDate = date => {
    const { t } = this.props;
    return date ? dateFormat(date, t) : '-';
  };

  renderValue = value => {
    if (value === undefined || value === null || value === '') {
      return '-';
    }
    return value;
  };

  renderNotificationMessage = () => {
    const { errors, t } = this.props;
    if (errors.length > 0 && errors[0].messages.length > 0) {
      return errors[0].messages[0];
    }
    return t('common.failed_bulk_action', { count: 1 });
  };

  renderActions = () => {
    const {
      actionLoading,
      classes,
      data,
      dismissReport,
      resolveReport,
      t,
    } = this.props;
    if (!data.id || data.status !== reviewReportStatus.OPEN) {
      return null;
    }
    return (
      <Card>
        {actionLoading && (
          <div className={classes.actionsLoader}>
            <LoadingAnimation />
          </div>
        )}
        <div className={classes.permissionsContainer}>
          <WithPermission allowPermissions={resolvePermissions}>
            <Button
              data-cy="resolveReviewReport"
              color="success"
              id="resolve-review-report"
              onClick={() => resolveReport([data.id])}
            >
              <Done />
              <span>{t('review_report.actions.resolve')}</span>
            </Button>
          </WithPermission>
          <WithPermission allowPermissions={dismissPermissions}>
            <Button
              data-cy="dismissReviewReport"
              color="danger"
              id="dismiss-review-report"
              onClick={() => dismissReport([data.id])}
            >
              <Cancel />
              <span>{t('review_report.actions.dismiss')}</span>
            </Button>
          </WithPermission>
        </div>
      </Card>
    );
  };

  render() {
    const {
      classes,
      closeNotification,
      data,
      loading,
      refresh,
      showNotification,
      t,
    } = this.props;
    const { review } = data;
    if (loading) {
      return (
        <div className={classes.actionsLoader}>
          <LoadingAnimation />
        </div>
      );
    }
    return (
      <React.Fragment>
        <Snackbar
          place="tc"
          color="danger"
          message={this.renderNotificationMessage()}
          open={showNotification}
          closeNotification={closeNotification}
          close
        />
        <GridContainer>
          <GridItem xs={12} sm={6}>
            <h3 className={classes.title}>
              {t('review_report.details.title')}
            </h3>
          </GridItem>
          <GridItem xs={12} sm={6}>
            <GridContainer justify="flex-end" alignItems="center">
              <GridItem>
                <Button
                  color="primary"
                  simple
                  className={classes.showFilters}
                  onClick={() => refresh()}
                  id="refresh"
                  key="refresh"
                >
                  <Refresh />
                  {t('common.refresh')}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} md={8}>
            <Card>
              <CardBody>
                <SectionDivider
                  label={t('review_report.details.report_section')}
                >
                  <GridContainer>
                    <GridItem xs={12} sm={4}>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.id')}</h6>
                        <p id={`review-report-${data.id}`}>{data.id}</p>
                      </div>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.status')}</h6>
                        <p>{this.renderStatus(data.status)}</p>
                      </div>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.reason')}</h6>
                        <p>{this.renderReason(data.reason)}</p>
                      </div>
                    </GridItem>
                    <GridItem xs={12} sm={8}>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.comment')}</h6>
                        <p>{data.comment || t('common.unknown')}</p>
                      </div>
                    </GridItem>
                  </GridContainer>
                </SectionDivider>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <SectionDivider
                  label={t('review_report.details.review_section')}
                >
                  <GridContainer>
                    <GridItem xs={12} sm={4}>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.review_id')}</h6>
                        {review ? (
                          <WithPermission
                            alt={<p>{this.renderValue(review.id)}</p>}
                            allowPermissions={viewReviewPermissions}
                          >
                            <Button
                              id={`linked-review-${review.id}`}
                              simple
                              color="primary"
                              style={{
                                padding: 0,
                                margin: 0,
                                textAlign: 'left',
                              }}
                              className={classes.adLinks}
                              onClick={() => this.handleReadReview(review.id)}
                            >
                              {review.id}
                            </Button>
                          </WithPermission>
                        ) : (
                          <p>-</p>
                        )}
                      </div>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.review_status')}</h6>
                        <p>{this.renderValue(review && review.status)}</p>
                      </div>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.rate')}</h6>
                        <p>{this.renderValue(review && review.rate)}</p>
                      </div>
                    </GridItem>
                    <GridItem xs={12} sm={8}>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.review_comment')}</h6>
                        <p>{this.renderValue(review && review.comment)}</p>
                      </div>
                      <div className={classes.userDetails}>
                        <h6>{t('review_report.fields.reviewed_by')}</h6>
                        {this.renderUser(
                          review && review.reviewedBy,
                          'review-reviewed-by',
                        )}
                      </div>
                    </GridItem>
                  </GridContainer>
                </SectionDivider>
              </CardBody>
            </Card>
          </GridItem>
          <GridItem xs={12} md={4}>
            {this.renderActions()}
            <Card>
              <CardBody>
                <SectionDivider
                  label={t('review_report.details.users_section')}
                >
                  <div className={classes.userDetails}>
                    <h6>{t('review_report.fields.reporter')}</h6>
                    {this.renderUser(data.reporter, 'reporter')}
                  </div>
                  <div className={classes.userDetails}>
                    <h6>{t('review_report.fields.reviewer')}</h6>
                    {this.renderUser(data.reviewer, 'reviewer')}
                  </div>
                  <div className={classes.userDetails}>
                    <h6>{t('review_report.fields.reviewee')}</h6>
                    {this.renderUser(data.reviewee, 'reviewee')}
                  </div>
                  <div className={classes.userDetails}>
                    <h6>{t('review_report.fields.reviewed_by')}</h6>
                    {this.renderUser(data.reviewedBy, 'reviewed-by')}
                  </div>
                </SectionDivider>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <SectionDivider
                  label={t('review_report.details.dates_section')}
                >
                  <div className={classes.userDetails}>
                    <h6>{t('review_report.fields.created_on')}</h6>
                    <p>{this.renderDate(data.createdOn)}</p>
                  </div>
                  <div className={classes.userDetails}>
                    <h6>{t('review_report.fields.updated_on')}</h6>
                    <p>{this.renderDate(data.updatedOn)}</p>
                  </div>
                  <div className={classes.userDetails}>
                    <h6>{t('review_report.fields.reviewed_on')}</h6>
                    <p>{this.renderDate(data.reviewedOn)}</p>
                  </div>
                </SectionDivider>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

ReviewReportDetails.propTypes = {
  actionLoading: PropTypes.bool.isRequired,
  classes: PropTypes.instanceOf(Object).isRequired,
  closeNotification: PropTypes.func.isRequired,
  data: PropTypes.shape({}).isRequired,
  dismissReport: PropTypes.func.isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  loading: PropTypes.bool.isRequired,
  refresh: PropTypes.func.isRequired,
  resolveReport: PropTypes.func.isRequired,
  showNotification: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

const ReviewReportDetailsWrapper = props => <ReviewReportDetails {...props} />;

export default withTranslation()(
  withStyles(userProfileStyles)(ReviewReportDetailsWrapper),
);
